<template>
  <div id="app">
    <TheHeader :isHomePath="isHomePath" />
    <!-- for template building -->
    <main role="main" class="t-main">
      <router-view />
    </main>
    <TheFooter />
    <ButtonScrollToTop bottom="45" right="24" :visibleoffset="600" />
  </div>
</template>

<script>
import TheHeader from "@/components/TheHeader";
import TheFooter from "@/components/TheFooter";
import ButtonScrollToTop from "@/components/ButtonScrollToTop";

import hero from "@/assets/images/home/hero.jpeg";

export default {
  name: "TempleApp",
  metaInfo: {
    titleTemplate: (titleChunk) => {
      return titleChunk ? `${titleChunk} | 臺南宗教藝術` : "臺南宗教藝術";
    },
    meta: [
      // twitter: 只要 card, site, creator
      // 剩下的資料跟從 open graph protocol
      {
        vmid: "twitter:card",
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        vmid: "twitter:site",
        name: "twitter:site",
        content: "臺南宗教藝術",
      },
      {
        vmid: "twitter:creator",
        name: "twitter:creator",
        creator: "臺南市政府觀光旅遊局",
      },
      // facebook open graph
      {
        vmid: "og:site_name",
        property: "og:site_name",
        content: "臺南宗教藝術",
      },
      { vmid: "og:type", property: "og:type", content: "website" },
      {
        vmid: "og:locale",
        property: "og:locale",
        content: "zh_TW",
      },
      {
        vmid: "og:url",
        property: "og:url",
        content: "https://www.tainan-temple-tour.net/",
      },
      {
        vmid: "og:title",
        property: "og:title",
        content: "來一場臺南式的宗教藝術之旅",
      },
      {
        vmid: "og:description",
        property: "og:description",
        content:
          "沒有一定的參觀方式，也不需要任何門票，無意中在臺南散步見到了一座廟宇，只要有欣賞跟尊重的心意，都歡迎走進來看看。臺南人為了把最好獻給護佑鄉里的神明，廟宇的每一處幾乎沒有留白，都被填滿了華麗的工藝。你可以拉遠看、靠近看、低頭看、抬頭看，每間廟宇都有百個以上的圖樣造型值得慢慢欣賞，看累了可以在廟兩旁的板條凳上休憩一下。欣賞完廟宇之美後也建議到附近的景點與小店去走一走，吃個肉圓買包蜜餞，或再前往另一座鄰近的美廟，讓一場臺南式的廟宇小旅行更加圓滿。",
      },
      {
        vmid: "og:image",
        property: "og:image",
        content: hero,
      },
      {
        vmid: "og:image:width",
        property: "og:image:width",
        content: 600,
      },
      {
        vmid: "og:image:height",
        property: "og:image:height",
        content: 315,
      },
    ],
  },
  computed: {
    isHomePath() {
      return this.$route.path === "/";
    },
    isFestivalYear() {
      return this.$route.meta.isRenderingFestivalTheme;
    },
    initialWindowWidth() {
      return (
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth
      );
    },
    windowWidth() {
      return this.$store.getters.windowWidth;
    },
    // 未來若有其他需要公告事項，再將 hasNoticedUserForMaintenance 打開做判斷
    //  hasNoticedUserForMaintenance() {
    //   return localStorage.getItem("hasNoticedUserForMaintenance");
    // },
  },
  mounted() {
    // 掛載完成後先設定初始視窗寬度
    this.$store.commit("setWindowWidth", this.initialWindowWidth);

    this.$nextTick(() => {
      // 如果重新調整寬度，就拿新的視窗寬度
      window.addEventListener("resize", this.onResize);
    });

    // 未來若有其他需要公告事項，再將以下註解打開做判斷
    // 讀取 key 判斷公告是否已提醒過 User
    // if (!this.hasNoticedUserForMaintenance) {
    //   this.onNoticedUserForMaintenance();
    // }

    // a11y: 讓 access key 在鍵盤上有對應作用
    window.addEventListener('keydown', this.handleKeydownAndGoToAnchorPoint);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener('keydown', this.handleKeydownAndGoToAnchorPoint);
  },
  methods: {
    axeRun() {
      this.$axe.run({
        clearConsole: true,
        element: document, // or document, document.querySelector('.selector'), ...
        label: "App component",
      });
    },
    onResize() {
      const windowWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;

      this.$store.commit("setWindowWidth", windowWidth);
    },
    handleKeydownAndGoToAnchorPoint(event) {
      // 鍵盤按下 alt+u，頁面滑到頂端
      // 鍵盤按下 alt+c，頁面滑到主要內容
      // 鍵盤按下 alt+z，頁面滑到頁尾
      const navigation = document.getElementById('navigation');
      const mainContent = document.getElementById('mainContent');
      const footer = document.getElementById('footer');

      // key 會受大小寫影響，code 不會受大小寫影響
      // 頁面滑到頂端
      if (event.altKey && event.code === 'KeyU') {
        this.scrollToAndFocus(navigation, 0);
      }

      // 頁面滑到主要內容
      if (event.altKey && event.code === 'KeyC') {
        if (mainContent) {
          const offset = mainContent.offsetTop - 100;
          this.scrollToAndFocus(mainContent, offset);
        }
      }

      // 頁面滑到頁尾
      if (event.altKey && event.code === 'KeyZ') {
        if (footer) {
          const offset = footer.offsetTop;
          this.scrollToAndFocus(footer, offset);
        }
      }
    },
    scrollToAndFocus(element, scrollTo) {
      window.scrollTo({ behavior: 'smooth', top: scrollTo });
      setTimeout(() => {
        if (element) {
          element.focus();
        }
      }, 300);
    }
    // 未來若有其他需要公告事項，再將 onNoticedUserForMaintenance 啟用
    // onNoticedUserForMaintenance() {
    //   if (
    //     window.confirm(
    //       "【系統公告】網站將於 6/5（一） 10:00~14:00 進行維護，屆時無法提供瀏覽，敬請見諒，謝謝。"
    //     )
    //   ) {
    //     localStorage.setItem("hasNoticedUserForMaintenance", true);
    //   }
    // },
  },
  components: {
    TheHeader,
    TheFooter,
    ButtonScrollToTop,
  },
};
</script>
<style lang="scss">
html {
  background: $color-light-bg;
  color: $color-light-text;
  overflow-x: hidden; // for IE when using transform (translate) to make picture RWD and not be scaled down
}

.light {
  background: $color-light-bg;
  color: $color-light-text;

  #app {
    .t-main {
      padding-top: 64px;
    }

    .article {
      &-head {
        @include for-tablet {
          margin-bottom: 20px;
        }

        @include for-desktop {
          margin-bottom: 0;
        }
      }
      .paragraph__content {
        margin-top: 10px;

        @include for-desktop {
          margin-top: 20px;
        }

        h3 {
          font-size: 1.25rem;
          letter-spacing: 1.11px;
          color: $color-light-text;
          padding: 0 15px;

          @include for-desktop {
            letter-spacing: 1.22px;
          }
        }

        p {
          font-size: 1rem;
          line-height: 1.5;
          color: $color-light-paragraph;
          padding: 0 15px;
        }

        h3,
        p {
          max-width: 620px;
          margin: auto;

          @include for-tablet {
            padding-left: 0;
            padding-right: 0;
          }
        }
      }

      .other-links {
        margin: 30px auto 0;
        max-width: 620px;

        @include for-tablet {
          padding: 0 15px;
          max-width: 620px;
          padding-left: 0;
          padding-right: 0;
        }
      }

      .other-link {
        background-image: none;
        text-align: left;

        &::before,
        &::after {
          background-image: none;
        }

        &__link {
          display: inline-block;
          padding: 1px 0;
          color: $color-light-text;
          text-decoration: underline;
          font-weight: 500;

          &::before {
            background: none;
          }
        }
      }
    }
  }
}

#app {
  .t-main {
    padding-top: 63px;
  }

  .accesskey-c {
    position: relative;
    z-index: getLayerIndex(accesskey);
    left: 3px;

    &--light {
      top: 280px;
      @include for-tablet {
        top: 250px;
      }
    }
  }

  .swiper-wrapper {
    width: 100%;
    margin: auto;
    max-width: 1440px;
  }

  .swiper-pagination-bullet {
    width: 12px;
    height: 12px;
    background-color: $color-circle-bg;
  }

  .swiper-container-multirow > .swiper-wrapper {
    justify-content: flex-start;
  }

  /* Override for vue2-google-map */
  .vue-map {
    border-radius: 5px;
  }

  /* For Lazy-loading */
  .loaded {
    opacity: 1;
  }
}
</style>
